import React from 'react';
import { Alert } from '@mui/material';
import type { UseFormRegister, FieldErrors } from 'react-hook-form';
import { Button, Input } from '../../../ui';
import { ErrorIcon } from '../../../ui/icons';
import ReCaptchaV3 from '../../../Wrappers/ReCaptchaV3';
import { assetUrl } from '../../../../utils/webConfig';
import type { PasswordFormValues } from './usePasswordStep';
import { DISABLE_CAPTCHA } from '../../../../utils';

type AuthPasswordStepViewProps = {
    register: UseFormRegister<PasswordFormValues>;
    errors: FieldErrors<PasswordFormValues>;
    loading?: boolean;
    showPassword: boolean;
    error?: string | null;
    dismissError?: () => void;
    onToggleShowPassword?: () => void;
    onSubmit?: React.FormEventHandler<HTMLFormElement>;
};

export const AuthPasswordStepView: React.FC<AuthPasswordStepViewProps> = ({
    register,
    errors,
    loading,
    showPassword,
    error,
    dismissError,
    onToggleShowPassword,
    onSubmit,
}) => (
    <form id="auth-password-form" onSubmit={onSubmit}>
        <div
            id="auth-password-step"
            className="flex flex-col mt-8 w-full min-h-[412px]"
        >
            <div
                id="auth-password-heading"
                className="text-body-large text-primary mb-6"
            >
                Enter a password to keep track of your gift
            </div>
            <div id="auth-password-input-container" className="relative">
                <style id="auth-password-visibility-styles">{`
                    input[type="password"]::-ms-reveal,
                    input[type="password"]::-ms-clear,
                    input[type="password"]::-webkit-contacts-auto-fill-button,
                    input[type="password"]::-webkit-credentials-auto-fill-button { display: none; }
                `}</style>
                <Input
                    {...register('password', {
                        required: 'Password is required',
                        minLength: {
                            value: 8,
                            message: 'Password must be at least 8 characters',
                        },
                    })}
                    type={showPassword ? 'text' : 'password'}
                    label="Password"
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    id="auth-password"
                />
                <button
                    id="auth-password-visibility-button"
                    type="button"
                    className="absolute right-3 top-[50%] translate-y-[-50%] text-secondary border-none bg-transparent"
                    onClick={onToggleShowPassword}
                    tabIndex={-1}
                >
                    <img
                        id="auth-password-visibility-icon"
                        src={assetUrl(
                            `/images/svg/${showPassword ? 'pass_hide' : 'pass_show'}.svg`,
                        )}
                        alt={showPassword ? 'Hide password' : 'Show password'}
                        width={17}
                        height={17}
                    />
                </button>
            </div>
            <ul
                id="auth-password-benefits"
                className="text-body-xsmall text-secondary mt-4 list-disc pl-4 flex flex-col gap-1"
            >
                <li id="auth-password-benefit-quick-giving">
                    Give next time in 3 easy taps
                </li>
                <li id="auth-password-benefit-giving-history">
                    Keep track of your giving history in one place
                </li>
            </ul>
            {error ? (
                <Alert
                    id="auth-password-error-alert"
                    className="mt-6"
                    data-testid="password-error-alert"
                    severity="error"
                    onClose={dismissError}
                    iconMapping={{ error: <ErrorIcon /> }}
                >
                    {error}
                </Alert>
            ) : null}
            {!DISABLE_CAPTCHA && <ReCaptchaV3 hideText={true} />}
            <Button
                id="auth-password-submit-button"
                type="submit"
                className="w-full mt-auto"
                loading={loading}
            >
                Sign up
            </Button>
        </div>
    </form>
);
